/** Colour helpers for the heat strip (UX-SPEC §12.5): interpolation in OKLab between two sRGB stops. */

type Rgb = [number, number, number];

const hexToRgb = (h: string): Rgb => {
	const n = Number.parseInt(h.replace('#', ''), 16);
	return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toLinear = (c: number): number => {
	const v = c / 255;
	return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const fromLinear = (v: number): number => {
	const c = v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
	return Math.max(0, Math.min(255, Math.round(c * 255)));
};

function toOklab([r, g, b]: Rgb): Rgb {
	const lr = toLinear(r);
	const lg = toLinear(g);
	const lb = toLinear(b);
	const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
	const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
	const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
	return [
		0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
		1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
		0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
	];
}

function fromOklab([L, a, b]: Rgb): Rgb {
	const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
	const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
	const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
	return [
		fromLinear(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
		fromLinear(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
		fromLinear(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
	];
}

/** The two ends of the evidence ramp (`--ev-300` at index 0, `--ev-700` at index 100; tokens.css). */
const EV_300 = toOklab(hexToRgb('#8ba5d4'));
const EV_700 = toOklab(hexToRgb('#1a3363'));

const cache = new Map<number, string>();

/** The strip fill for an index 0–100, quantised to whole numbers. */
export function evidenceFill(index: number): string {
	const t = Math.max(0, Math.min(100, Math.round(index)));
	const hit = cache.get(t);
	if (hit) return hit;
	const k = t / 100;
	const [r, g, b] = fromOklab([
		EV_300[0] + (EV_700[0] - EV_300[0]) * k,
		EV_300[1] + (EV_700[1] - EV_300[1]) * k,
		EV_300[2] + (EV_700[2] - EV_300[2]) * k,
	]);
	const css = `rgb(${r}, ${g}, ${b})`;
	cache.set(t, css);
	return css;
}
